/**
 * LobbyChat — Public chat bottom-sheet for the lobby (text-only for now).
 *
 * Features today: bubbles with avatar rings, system pills, expand/collapse handle,
 *                 input + send button, auto-scroll-to-bottom on append (when near bottom).
 *
 * Forward seam for stickers: ChatMessage carries a `kind` field. Add a 'sticker'
 * branch in renderMessage() when Lottie support lands.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { ChatMessage } from '../../../services/LobbyService';

interface LobbyChatEvents {
  'send': { text: string };
  /** A chat sender's avatar/name was tapped — open their player card. */
  'profile:tap': { playerId: string; username?: string; picture?: string };
}

const MAX_RENDERED_MESSAGES = 200;
const TRIM_TO_MESSAGES = 150;
const NEAR_BOTTOM_PX = 60;
const UNREAD_DISPLAY_CAP = 99;

export class LobbyChat extends EventEmitter<LobbyChatEvents> {
  public element: HTMLElement;
  private bodyEl: HTMLElement;
  private inputEl: HTMLInputElement;
  private newPillEl: HTMLButtonElement;
  private newPillCountEl: HTMLElement;
  private expanded = false;
  private unreadCount = 0;
  /** Used to right-align bubbles whose sender is the local player. */
  private localPlayerId: string | null = null;

  constructor() {
    super();
    LobbyChat.injectStyles();

    this.element = document.createElement('section');
    this.element.className = 'sdl-lchat';
    this.element.innerHTML = `
      <div class="sdl-lchat-handle" data-handle>
        <div class="sdl-lchat-grip"></div>
        <div class="sdl-lchat-handle-left">
          <div class="sdl-lchat-label">PUBLIC CHAT</div>
          <div class="sdl-lchat-live">LIVE</div>
        </div>
        <div class="sdl-lchat-toggle" data-toggle>⌃</div>
      </div>
      <div class="sdl-lchat-bodywrap">
        <div class="sdl-lchat-body" data-body></div>
        <button class="sdl-lchat-newpill" type="button" data-newpill aria-label="Jump to latest">
          ↓&nbsp;<span data-newcount>1</span>&nbsp;NEW
        </button>
      </div>
      <div class="sdl-lchat-input">
        <input type="text" placeholder="Talk smack..." maxlength="200" data-input />
        <button class="sdl-lchat-send" type="button" data-send aria-label="Send" data-sound="button">➤</button>
      </div>
    `;

    this.bodyEl = this.element.querySelector('[data-body]') as HTMLElement;
    this.inputEl = this.element.querySelector('[data-input]') as HTMLInputElement;
    this.newPillEl = this.element.querySelector('[data-newpill]') as HTMLButtonElement;
    this.newPillCountEl = this.element.querySelector('[data-newcount]') as HTMLElement;

    const handle = this.element.querySelector('[data-handle]') as HTMLElement;
    handle.addEventListener('click', (e) => {
      // Don't toggle when interacting with the input bar
      if ((e.target as HTMLElement).closest('.sdl-lchat-input')) return;
      this.toggleExpanded();
    });

    const sendBtn = this.element.querySelector('[data-send]') as HTMLElement;
    sendBtn.addEventListener('click', () => this.fireSend());
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.fireSend();
      }
    });
    // Tapping into the input expands the chat (more space to read while typing)
    this.inputEl.addEventListener('focus', () => {
      if (!this.expanded) this.setExpanded(true);
    });

    // "Jump to latest" pill: instantly scroll to the bottom and clear unread.
    this.newPillEl.addEventListener('click', () => {
      this.scrollToBottom(true);
      this.clearUnread();
    });

    // Tap another player's avatar/name in chat → open their player card.
    this.bodyEl.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.sdl-lchat-avatar, .sdl-lchat-name')) return;
      const row = target.closest<HTMLElement>('.sdl-lchat-msg');
      const playerId = row?.dataset.playerId;
      if (!playerId || row!.classList.contains('sdl-self')) return; // skip own messages
      this.emit('profile:tap', {
        playerId,
        username: row!.dataset.username || undefined,
        picture: row!.dataset.picture || undefined,
      });
    });

    // When the user scrolls themselves back to the bottom (manually catching
    // up), clear any pending unread count so the pill goes away.
    this.bodyEl.addEventListener('scroll', () => {
      if (this.unreadCount > 0 && this.isNearBottom()) this.clearUnread();
    }, { passive: true });
  }

  /**
   * Tell the chat which player id is "you" so messages from that id render
   * right-aligned with the gold-tinted self bubble. Re-renders existing
   * messages so the alignment updates immediately when auth lands after
   * messages have already arrived.
   */
  setLocalPlayerId(playerId: string | null): void {
    if (this.localPlayerId === playerId) return;
    this.localPlayerId = playerId;
    // Patch in place: toggle the sdl-self class on existing bubbles instead
    // of re-rendering, so scroll position and animations are preserved.
    this.bodyEl.querySelectorAll<HTMLElement>('.sdl-lchat-msg').forEach((el) => {
      const isSelf = playerId !== null && el.dataset.playerId === playerId;
      el.classList.toggle('sdl-self', isSelf);
    });
  }

  setMessages(messages: ChatMessage[]): void {
    this.bodyEl.innerHTML = '';
    messages.forEach((m) => this.bodyEl.appendChild(this.renderMessage(m)));
    this.scrollToBottom(true);
  }

  appendMessage(message: ChatMessage): void {
    const wasNearBottom = this.isNearBottom();
    this.bodyEl.appendChild(this.renderMessage(message));
    this.trimIfTooLong();
    // Auto-scroll only when the chat is expanded AND the user is at the
    // bottom — the live-reader case. While collapsed, the visible area is
    // tiny and any scroll yanks the currently-visible message out of view,
    // so we treat the collapsed state like scrolled-up: surface the pill
    // and let the user choose to expand / catch up.
    if (wasNearBottom && this.expanded) {
      this.scrollToBottom();
    } else {
      this.unreadCount++;
      this.updatePill();
    }
  }

  setExpanded(value: boolean): void {
    if (this.expanded === value) return;
    this.expanded = value;
    this.element.classList.toggle('sdl-lchat-expanded', value);
    if (value) {
      // Expanding implies "I want to see the latest" — snap to bottom and
      // clear any pending unread once the height transition has settled.
      window.setTimeout(() => {
        this.scrollToBottom(true);
        this.clearUnread();
      }, 400);
    }
  }

  toggleExpanded(): void {
    this.setExpanded(!this.expanded);
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  // ---------- Unread pill ----------

  private clearUnread(): void {
    if (this.unreadCount === 0) return;
    this.unreadCount = 0;
    this.updatePill();
  }

  private updatePill(): void {
    if (this.unreadCount <= 0) {
      this.newPillEl.classList.remove('visible');
      return;
    }
    const display = this.unreadCount > UNREAD_DISPLAY_CAP
      ? `${UNREAD_DISPLAY_CAP}+`
      : String(this.unreadCount);
    this.newPillCountEl.textContent = display;
    this.newPillEl.classList.add('visible');
  }

  // ---------- Internal ----------

  private fireSend(): void {
    const text = this.inputEl.value.trim();
    if (!text) return;
    this.inputEl.value = '';
    this.emit('send', { text });
  }

  private renderMessage(m: ChatMessage): HTMLElement {
    if (m.kind === 'system') {
      const el = document.createElement('div');
      el.className = 'sdl-lchat-system';
      el.textContent = m.text;
      return el;
    }
    // 'text' bubble
    const wrap = document.createElement('div');
    wrap.className = 'sdl-lchat-msg';
    const isSelf = !!m.playerId && m.playerId === this.localPlayerId;
    if (isSelf) wrap.classList.add('sdl-self');
    const ring = m.ringColor || 'silver';
    const initials = (m.username || '??').slice(0, 2).toUpperCase();
    const avatarInner = m.picture
      ? `<img class="sdl-lchat-avatar-img" src="${escapeHtml(m.picture)}" alt="">`
      : escapeHtml(initials);
    wrap.innerHTML = `
      <div class="sdl-lchat-avatar" data-ring="${ring}">${avatarInner}</div>
      <div class="sdl-lchat-stack">
        <div class="sdl-lchat-name" data-ring="${ring}">${escapeHtml(m.username || '')}</div>
        <div class="sdl-lchat-bubble">${escapeHtml(m.text)}</div>
      </div>
    `;
    if (m.playerId) {
      wrap.dataset.playerId = m.playerId;
      wrap.dataset.username = m.username || '';
      if (m.picture) wrap.dataset.picture = m.picture;
    }
    return wrap;
  }

  private trimIfTooLong(): void {
    const count = this.bodyEl.childElementCount;
    if (count <= MAX_RENDERED_MESSAGES) return;
    const drop = count - TRIM_TO_MESSAGES;
    for (let i = 0; i < drop; i++) {
      const first = this.bodyEl.firstElementChild;
      if (!first) break;
      first.remove();
    }
  }

  private isNearBottom(): boolean {
    const { scrollTop, scrollHeight, clientHeight } = this.bodyEl;
    return scrollHeight - (scrollTop + clientHeight) < NEAR_BOTTOM_PX;
  }

  private scrollToBottom(immediate = false): void {
    const apply = () => { this.bodyEl.scrollTop = this.bodyEl.scrollHeight; };
    if (immediate) apply();
    else requestAnimationFrame(apply);
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-lchat-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-lchat-styles';
    style.textContent = `
      .sdl-lchat {
        flex-shrink: 0;
        background: linear-gradient(180deg, rgba(8,8,10,0.85) 0%, var(--dark-bg) 30%);
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);
        border-top: 1px solid var(--glass-border);
        transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        max-height: 280px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding-bottom: env(safe-area-inset-bottom);
      }
      .sdl-lchat-expanded { max-height: 75vh; }

      .sdl-lchat-handle {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 16px 6px;
        cursor: pointer;
        flex-shrink: 0;
      }
      .sdl-lchat-grip {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 36px;
        height: 3px;
        border-radius: 2px;
        background: rgba(255,255,255,0.15);
        top: 5px;
      }
      .sdl-lchat-handle-left {
        display: flex; align-items: center; gap: 8px;
        margin-top: 6px;
      }
      .sdl-lchat-label {
        font-family: var(--font-display);
        font-size: 13px;
        letter-spacing: 2.2px;
        color: rgba(255,255,255,0.7);
      }
      .sdl-lchat-live {
        display: flex; align-items: center; gap: 5px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 1px;
        color: var(--green);
      }
      .sdl-lchat-live::before {
        content: '';
        width: 6px; height: 6px;
        background: var(--green);
        border-radius: 50%;
        box-shadow: 0 0 6px var(--green);
        animation: sdl-lchat-pulse 1.6s ease-in-out infinite;
      }
      @keyframes sdl-lchat-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.85); }
      }
      .sdl-lchat-toggle {
        margin-top: 6px;
        font-size: 14px;
        color: rgba(255,255,255,0.45);
        transition: transform 0.3s ease;
      }
      .sdl-lchat-expanded .sdl-lchat-toggle { transform: rotate(180deg); }

      /* Wrapper hosts the body + the floating "↓ N NEW" pill so the pill
         can be absolutely positioned at the bottom of the visible chat
         area without depending on the input row's height. */
      .sdl-lchat-bodywrap {
        flex: 1;
        position: relative;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .sdl-lchat-body {
        flex: 1;
        position: relative;
        padding: 6px 12px 10px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 0;
      }

      .sdl-lchat-newpill {
        all: unset;
        position: absolute;
        bottom: 12px;
        left: 50%;
        display: flex;
        align-items: center;
        padding: 7px 14px;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border: 1px solid var(--gold-light);
        border-radius: 999px;
        color: #1a1300;
        font-family: var(--font-display);
        font-size: 13px;
        letter-spacing: 1.5px;
        box-shadow: 0 4px 14px rgba(255,215,0,0.3),
                    inset 0 1px 0 rgba(255,255,255,0.3);
        cursor: pointer;
        z-index: 2;
        opacity: 0;
        pointer-events: none;
        transform: translate(-50%, 12px);
        transition: opacity 0.22s ease, transform 0.22s ease;
        white-space: nowrap;
      }
      .sdl-lchat-newpill.visible {
        opacity: 1;
        pointer-events: auto;
        transform: translate(-50%, 0);
      }
      .sdl-lchat-newpill:active {
        transform: translate(-50%, 0) scale(0.96);
      }
      .sdl-lchat-body::-webkit-scrollbar { width: 0; }
      .sdl-lchat-body::before {
        content: '';
        position: sticky;
        top: 0;
        display: block;
        height: 14px;
        background: linear-gradient(180deg, var(--dark-bg), transparent);
        margin-bottom: -14px;
        pointer-events: none;
        z-index: 1;
      }

      .sdl-lchat-msg {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        max-width: 100%;
      }
      .sdl-lchat-msg:not(.sdl-self) .sdl-lchat-avatar,
      .sdl-lchat-msg:not(.sdl-self) .sdl-lchat-name { cursor: pointer; }
      .sdl-lchat-avatar {
        flex-shrink: 0;
        width: 30px; height: 30px;
        border-radius: 50%;
        background: linear-gradient(135deg, #2a2a35, #15151a);
        display: flex; align-items: center; justify-content: center;
        font-family: var(--font-display);
        font-size: 13px;
        letter-spacing: 0.5px;
        border: 2px solid var(--concrete);
        overflow: hidden;
      }
      .sdl-lchat-avatar-img {
        width: 100%; height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }
      .sdl-lchat-avatar[data-ring="gold"]    { box-shadow: 0 0 0 1.5px rgba(255,215,0,0.5);  color: var(--gold); }
      .sdl-lchat-avatar[data-ring="cyan"]    { box-shadow: 0 0 0 1.5px rgba(0,245,255,0.5);  color: var(--cyan); }
      .sdl-lchat-avatar[data-ring="magenta"] { box-shadow: 0 0 0 1.5px rgba(255,0,128,0.5);  color: var(--magenta); }
      .sdl-lchat-avatar[data-ring="green"]   { box-shadow: 0 0 0 1.5px rgba(0,255,136,0.5);  color: var(--green); }
      .sdl-lchat-avatar[data-ring="purple"]  { box-shadow: 0 0 0 1.5px rgba(165,107,255,0.5); color: var(--purple); }
      .sdl-lchat-avatar[data-ring="silver"]  { box-shadow: 0 0 0 1.5px rgba(216,216,224,0.4); color: #fff; }

      .sdl-lchat-stack {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
        max-width: calc(100% - 42px);
      }
      .sdl-lchat-name {
        font-family: var(--font-display);
        font-size: 11px;
        letter-spacing: 1.5px;
        padding: 0 4px;
        color: rgba(255,255,255,0.6);
      }
      .sdl-lchat-name[data-ring="gold"]    { color: var(--gold); }
      .sdl-lchat-name[data-ring="cyan"]    { color: var(--cyan); }
      .sdl-lchat-name[data-ring="magenta"] { color: var(--magenta); }
      .sdl-lchat-name[data-ring="green"]   { color: var(--green); }
      .sdl-lchat-name[data-ring="purple"]  { color: var(--purple); }
      .sdl-lchat-name[data-ring="silver"]  { color: rgba(255,255,255,0.7); }

      .sdl-lchat-bubble {
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 14px 14px 14px 4px;
        padding: 9px 12px;
        font-family: var(--font-body);
        font-size: 13.5px;
        line-height: 1.35;
        color: rgba(255,255,255,0.92);
        word-wrap: break-word;
        max-width: 100%;
      }

      .sdl-lchat-msg.sdl-self { flex-direction: row-reverse; }
      .sdl-lchat-msg.sdl-self .sdl-lchat-stack { align-items: flex-end; }
      .sdl-lchat-msg.sdl-self .sdl-lchat-bubble {
        background: linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,215,0,0.08));
        border-color: rgba(255,215,0,0.3);
        border-radius: 14px 14px 4px 14px;
        color: #fff;
      }

      .sdl-lchat-system {
        align-self: center;
        font-family: var(--font-body);
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 1.4px;
        color: rgba(255,255,255,0.5);
        text-transform: uppercase;
        padding: 4px 12px;
        background: rgba(255,255,255,0.03);
        border: 1px solid var(--glass-border);
        border-radius: 999px;
        max-width: 80%;
        text-align: center;
      }

      .sdl-lchat-input {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px 10px;
        border-top: 1px solid var(--glass-border);
        background: rgba(8,8,10,0.5);
      }
      .sdl-lchat-input input {
        flex: 1;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 10px;
        padding: 10px 14px;
        color: white;
        font-family: var(--font-body);
        font-size: 14px;
        font-weight: 500;
        outline: none;
      }
      .sdl-lchat-input input::placeholder { color: rgba(255,255,255,0.3); }
      .sdl-lchat-input input:focus { border-color: rgba(255,215,0,0.3); }
      .sdl-lchat-send {
        all: unset;
        width: 40px; height: 40px;
        flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border-radius: 10px;
        font-size: 18px;
        color: #1a1300;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(255,215,0,0.2);
      }
      .sdl-lchat-send:active { transform: scale(0.94); }
    `;
    document.head.appendChild(style);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c] || c));
}
